import {
    ActionPerformed,
    PushNotificationSchema,
    PushNotifications,
    Token,
} from '@capacitor/push-notifications';
import {authenticatedFetch, getToken, setFirebaseMessagingToken} from './auth';
import * as Sentry from "@sentry/react";
import {App} from '@capacitor/app';

const API_URL = import.meta.env.VITE_API_URL || '';

App.addListener('resume', async () => {
    if (!__IS_CORDOVA__) {
        return;
    }
    await PushNotifications.removeAllDeliveredNotifications();
})

export async function syncFirebaseMessagingToken(token: string): Promise<void> {
    if (!__IS_CORDOVA__) {
        return;
    }
    if (!token) {
        return;
    }

    if (!getToken()) {
        // App may receive a device token before user login.
        return;
    }

    try {
        const response = await authenticatedFetch(`${API_URL}/api/participant/firebase-messaging-token`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({firebaseMessagingToken: token}),
        });

        if (!response.ok) {
            console.error('failed to sync firebase messaging token:', response.status);
        }
    } catch (error) {
        console.error('failed to sync firebase messaging token:', error);
    }
}

export async function initNotifications() {
    if (!__IS_CORDOVA__) {
        return;
    }
    const result = await PushNotifications.requestPermissions();
    console.log('push permissions:', result);
    if (result.receive === 'granted') {
        // Register with Apple / Google to receive push via APNS/FCM
        PushNotifications.register();
    } else {
        console.warn('push notification permission not granted');
        Sentry.captureMessage('push notification permission not granted', {
            extra: {
                result,
            },
        })
    }

    await PushNotifications.removeAllListeners()

    // On success, we should be able to receive notifications
    await PushNotifications.addListener('registration',
        (token: Token) => {
            console.log('push registration token:', token.value);
            setFirebaseMessagingToken(token.value);
            void syncFirebaseMessagingToken(token.value);
        }
    );

    // Some issue with our setup and push will not work
    await PushNotifications.addListener('registrationError',
        (error: any) => {
            console.error('push registration error:', error);
        }
    );

    // Show us the notification payload if the app is open on our device
    await PushNotifications.addListener('pushNotificationReceived',
        (notification: PushNotificationSchema) => {
            console.log('push notification received:', notification);
        }
    );

    // Method called when tapping on a notification
    await PushNotifications.addListener('pushNotificationActionPerformed',
        (notification: ActionPerformed) => {
            console.log('push notification action performed:', notification);
        }
    );
}

export async function removeNotificationListeners() {
    if (!__IS_CORDOVA__) {
        return;
    }
    await PushNotifications.removeAllListeners();
}

export async function unregisterNotifications() {
    if (!__IS_CORDOVA__) {
        return;
    }
    await PushNotifications.unregister();
}
